import { onBeforeUnmount, onMounted, ref } from 'vue';

const RESERVE_KEY = 'eva.keyboardReserve';
const KEYBOARD_MIN = 120;
const RESERVE_SHARE = 0.4;
const RESERVE_TOLERANCE = 12;

function readReserve(): number | null {
  try {
    const value = Number(localStorage.getItem(RESERVE_KEY));
    return Number.isFinite(value) && value >= KEYBOARD_MIN ? value : null;
  } catch {
    return null;
  }
}

function writeReserve(value: number): void {
  try {
    localStorage.setItem(RESERVE_KEY, String(value));
  } catch {
    return;
  }
}

export function useVisualViewport() {
  const viewportHeight = ref(typeof window === 'undefined' ? 0 : window.innerHeight);
  const keyboardInset = ref(0);

  const touch = window.matchMedia?.('(pointer: coarse)') ?? { matches: false };
  let frame = 0;
  let stableHeight = window.innerHeight;
  let stableWidth = window.innerWidth;
  let reserve = readReserve();

  function apply(): void {
    const vv = window.visualViewport;
    const height = vv?.height ?? window.innerHeight;
    const offsetTop = vv?.offsetTop ?? 0;

    if (window.innerWidth !== stableWidth) {
      stableWidth = window.innerWidth;
      stableHeight = height;
    }

    const shrunk = stableHeight - height - offsetTop;
    const open = touch.matches && shrunk > KEYBOARD_MIN;
    if (!open) stableHeight = height;

    const inset = open ? shrunk : Math.max(0, window.innerHeight - height - offsetTop);

    if (open && (reserve === null || Math.abs(reserve - inset) > RESERVE_TOLERANCE)) {
      reserve = Math.round(inset);
      writeReserve(reserve);
    }
    const reserved = touch.matches ? (reserve ?? Math.round(stableHeight * RESERVE_SHARE)) : 0;

    viewportHeight.value = stableHeight;
    keyboardInset.value = inset;

    const root = document.documentElement;
    root.style.setProperty('--app-viewport-height', `${stableHeight}px`);
    root.style.setProperty('--keyboard-inset', `${inset}px`);
    root.style.setProperty('--keyboard-reserve', `${reserved}px`);
  }

  function schedule(): void {
    if (frame) cancelAnimationFrame(frame);
    frame = requestAnimationFrame(apply);
  }

  onMounted(() => {
    apply();
    window.visualViewport?.addEventListener('resize', schedule);
    window.visualViewport?.addEventListener('scroll', schedule);
    window.addEventListener('resize', schedule);
    window.addEventListener('orientationchange', schedule);
  });

  onBeforeUnmount(() => {
    if (frame) cancelAnimationFrame(frame);
    window.visualViewport?.removeEventListener('resize', schedule);
    window.visualViewport?.removeEventListener('scroll', schedule);
    window.removeEventListener('resize', schedule);
    window.removeEventListener('orientationchange', schedule);
  });

  return { viewportHeight, keyboardInset };
}
